import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useI18n, useTranslate } from '../contexts/I18nContext';
import { useTerritory } from '../contexts/TerritoryContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTheme } from '../contexts/ThemeContext';
import { SEO } from '../components/SEO';
import { Footer } from '../components/Footer';
import { SeoLinks } from '../components/seo/SeoLinks';
import { optimizedImageUrl, optimizedSrcSet, DEFAULT_EVENT_IMAGE, handleEventImageError } from '../utils/imageUrl';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  MapPin, Calendar, ChevronRight, ChevronLeft, Search, Heart, Clock,
  Music, UtensilsCrossed, Users, Theater, Dumbbell, Rocket, Martini, Camera, Store,
  ShieldCheck, RefreshCw, UsersRound, LayoutGrid, Sparkles, Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---- Stale-while-revalidate cache helpers (sessionStorage) ----
// Landing lists change slowly; keep them for 2 min so a refresh feels instant.
const LANDING_CACHE_TTL_MS = 2 * 60 * 1000;
const readCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { t, v } = JSON.parse(raw);
    if (!t || Date.now() - t > LANDING_CACHE_TTL_MS) return null;
    return v;
  } catch { return null; }
};
const writeCache = (key, v) => {
  try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), v })); } catch { /* quota */ }
};

// ---- Category circle map (matches mockup palette) ----
const normalize = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const CATEGORY_MAP = [
  { match: ['musica', 'concierto', 'banda', 'show en vivo', 'show'], Icon: Music, bg: 'bg-violet-500', shadow: 'shadow-violet-500/40' },
  { match: ['gastronomia', 'comida', 'food', 'pub', 'restaurant'], Icon: UtensilsCrossed, bg: 'bg-orange-500', shadow: 'shadow-orange-500/40' },
  { match: ['familia', 'niño', 'nino', 'kids', 'infantil'], Icon: Users, bg: 'bg-pink-500', shadow: 'shadow-pink-500/40' },
  { match: ['teatro', 'cultura', 'arte', 'galeria', 'expo'], Icon: Theater, bg: 'bg-blue-500', shadow: 'shadow-blue-500/40' },
  { match: ['deporte', 'sport', 'fitness', 'bienestar'], Icon: Dumbbell, bg: 'bg-yellow-500', shadow: 'shadow-yellow-500/40' },
  { match: ['workshop', 'taller', 'curso', 'emprendimiento', 'innovacion', 'innovación'], Icon: Rocket, bg: 'bg-emerald-500', shadow: 'shadow-emerald-500/40' },
  { match: ['nocturna', 'fiesta', 'club', 'party'], Icon: Martini, bg: 'bg-purple-500', shadow: 'shadow-purple-500/40' },
  { match: ['turismo', 'tour', 'paseo', 'experiencia'], Icon: Camera, bg: 'bg-teal-500', shadow: 'shadow-teal-500/40' },
  { match: ['feria', 'mercado', 'market', 'comunidad'], Icon: Store, bg: 'bg-red-500', shadow: 'shadow-red-500/40' },
];

const getCategoryStyle = (cat) => {
  const name = typeof cat === 'string' ? cat : (cat?.name || '');
  const dbColor = typeof cat === 'object' ? cat?.color : null;
  const n = normalize(name);
  const match = CATEGORY_MAP.find((c) => c.match.some((kw) => n.includes(kw))) || {
    Icon: Sparkles,
    bg: 'bg-[#00CC66]',
    shadow: 'shadow-[#00CC66]/40',
  };
  // Prefer DB color (matches map view); fall back to Tailwind class
  if (dbColor) {
    return { ...match, bg: null, shadow: null, hexColor: dbColor };
  }
  return { ...match, hexColor: null };
};

// ---- Helpers for live status (Chile time) ----
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const getChileNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Santiago' }));

const isPermanentOpenNow = (operatingHours, manualOverrideClosed) => {
  if (manualOverrideClosed || !operatingHours) return false;
  const now = getChileNow();
  const today = operatingHours[DAY_KEYS[now.getDay()]];
  if (!today) return false;
  if (today.all_day) return true;
  try {
    const cur = now.getHours() * 60 + now.getMinutes();
    const [oH, oM] = today.open.split(':').map(Number);
    const [cH, cM] = today.close.split(':').map(Number);
    const o = oH * 60 + oM;
    const c = cH * 60 + cM;
    return c > o ? cur >= o && cur < c : cur >= o;
  } catch { return false; }
};

const isEventLiveNow = (event) => {
  if (event.is_online) return false; // treated separately
  if (event.is_permanent || event.is_recurring) {
    return isPermanentOpenNow(event.operating_hours, event.manual_override_closed);
  }
  try {
    const now = getChileNow();
    const start = new Date(event.start_date);
    const end = event.end_date ? new Date(event.end_date) : null;
    if (end) return now >= start && now <= end;
    return Math.abs(now - start) < 4 * 60 * 60 * 1000; // within 4h window
  } catch { return false; }
};

const formatEventTime = (event, tr = (s) => s) => {
  try {
    if (event.is_permanent || event.is_recurring) {
      const now = getChileNow();
      const today = event.operating_hours?.[DAY_KEYS[now.getDay()]];
      if (today?.all_day) return tr('Todo el día');
      if (today?.open && today?.close) return `${today.open} - ${today.close} ${tr('hrs')}`;
      if (today?.open) return `${today.open} ${tr('hrs')}`;
    }
    const start = new Date(event.start_date);
    return start.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Santiago' }) + ' ' + tr('hrs');
  } catch { return ''; }
};

const isEventToday = (event) => {
  if (!event) return false;
  try {
    const now = getChileNow();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const todayIso = `${yyyy}-${mm}-${dd}`;

    // Recurring with explicit dates: only "hoy" if today is in the list
    if (event.is_recurring && Array.isArray(event.recurring_dates) && event.recurring_dates.length > 0) {
      return event.recurring_dates.some(rd => (rd?.date || '').slice(0, 10) === todayIso);
    }

    // Permanent / weekly recurring: today is hoy only if operating_hours for today has open slot
    if (event.is_permanent || (event.is_recurring && event.operating_hours)) {
      const dayKey = DAY_KEYS[now.getDay()];
      const slot = event.operating_hours?.[dayKey];
      if (!slot) return false;
      if (slot.all_day) return true;
      return !!(slot.open || slot.close);
    }

    // Recurring without dates info → can't confirm
    if (event.is_recurring) return false;

    // One-off: same calendar day in Chile
    if (!event.start_date) return false;
    const start = new Date(event.start_date);
    return (
      now.getFullYear() === start.getFullYear() &&
      now.getMonth() === start.getMonth() &&
      now.getDate() === start.getDate()
    );
  } catch { return false; }
};

const shortDateLabel = (event) => {
  // For recurring events with dates → show the NEXT upcoming date
  try {
    const now = getChileNow();
    const months = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];

    if (event.is_recurring && Array.isArray(event.recurring_dates) && event.recurring_dates.length > 0) {
      const upcoming = event.recurring_dates
        .map(rd => rd?.date)
        .filter(Boolean)
        .map(d => new Date(`${d}T00:00:00-04:00`))
        .filter(d => !isNaN(d.getTime()) && d >= new Date(now.getFullYear(), now.getMonth(), now.getDate()))
        .sort((a, b) => a - b);
      const d = upcoming[0];
      if (d) return `${d.getDate()} ${months[d.getMonth()]}`;
    }

    if (!event.start_date) return null;
    const d = new Date(event.start_date);
    return `${d.getDate()} ${months[d.getMonth()]}`;
  } catch { return null; }
};

const getEventBadge = (event, tr = (s) => s, fp = null) => {
  if (isEventLiveNow(event)) return { label: tr('EN VIVO'), cls: 'bg-red-500 text-white', live: true };
  if (event.is_free || event.price === 0) return { label: tr('GRATIS'), cls: 'bg-emerald-500 text-white' };
  if (event.is_permanent && isPermanentOpenNow(event.operating_hours, event.manual_override_closed)) {
    return { label: tr('ABIERTO AHORA'), cls: 'bg-emerald-500 text-white' };
  }
  if (event.price > 0) {
    const priceStr = fp ? fp(event.price) : `$${Math.round(event.price).toLocaleString('es-CL')}`;
    return { label: `${tr('DESDE')} ${priceStr}`, cls: 'bg-amber-500 text-neutral-900' };
  }
  // Fallback: only mark as HOY when the event truly is today.
  if (isEventToday(event)) {
    return { label: tr('HOY'), cls: 'bg-white/15 text-white backdrop-blur' };
  }
  const date = shortDateLabel(event);
  if (date) return { label: date, cls: 'bg-white/15 text-white backdrop-blur' };
  return { label: tr('PRÓXIMAMENTE'), cls: 'bg-white/15 text-white backdrop-blur' };
};

// ---- Live event vertical poster card (hero rail) ----
const LiveEventCard = ({ event }) => {
  const { translateText } = useTranslate();
  const { formatPrice } = useCurrency();
  const badge = getEventBadge(event, translateText, formatPrice);
  return (
    <Link
      to={`/event/${event.id}`}
      className="group block relative shrink-0 w-[220px] md:w-[240px] h-[320px] md:h-[350px] rounded-2xl overflow-hidden bg-neutral-900 ring-1 ring-white/10 hover:ring-white/30 transition-all hover:-translate-y-1"
      data-testid={`live-event-${event.id}`}
    >
      {event.main_image ? (
        <img
          src={optimizedImageUrl(event.main_image, 400)}
          srcSet={optimizedSrcSet(event.main_image, [400, 800])}
          sizes="240px"
          alt={event.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
          decoding="async"
          style={event.image_position ? { objectPosition: `center ${event.image_position}` } : undefined}
          onError={handleEventImageError}
        />
      ) : (
        <img
          src={DEFAULT_EVENT_IMAGE}
          alt={event.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
          decoding="async"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/20" />

      {/* Badge */}
      <span className={`absolute top-3 left-3 ${badge.cls} text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-md inline-flex items-center gap-1 shadow-lg`}>
        {badge.live && (
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
          </span>
        )}
        {badge.label}
      </span>

      {/* Heart */}
      <span className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white/80 hover:text-pink-400 hover:bg-black/60 transition-colors">
        <Heart className="w-3.5 h-3.5" />
      </span>

      {/* Bottom info */}
      <div className="absolute bottom-0 left-0 right-0 p-3 text-left">
        {event.category_name && (
          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 mb-1 truncate">
            {translateText(event.category_name)}
          </p>
        )}
        <h3 className="text-sm md:text-[15px] font-bold text-white leading-tight line-clamp-2 mb-2">
          {translateText(event.name)}
        </h3>
        <p className="text-[11px] text-neutral-300 flex items-center gap-1 mb-0.5">
          <Clock className="w-3 h-3 text-neutral-400" />
          {formatEventTime(event, translateText)}
        </p>
        {event.commune_name && (
          <p className="text-[11px] text-neutral-300 flex items-center gap-1 truncate">
            <MapPin className="w-3 h-3 text-neutral-400" />
            <span className="truncate">
              {event.commune_name}
              {event.region_name && event.region_name !== event.commune_name && (
                <span className="text-neutral-400">, {event.region_name}</span>
              )}
            </span>
          </p>
        )}
      </div>
    </Link>
  );
};

// ---- Imperdibles rail card ----
const ImperdibleCard = ({ event }) => {
  const { translateText } = useTranslate();
  const { formatPrice } = useCurrency();
  const badge = getEventBadge(event, translateText, formatPrice);
  return (
    <Link
      to={`/event/${event.id}`}
      className="group block relative shrink-0 w-[260px] md:w-[280px] h-[340px] rounded-2xl overflow-hidden bg-neutral-900 ring-1 ring-white/10 hover:ring-white/30 transition-all hover:-translate-y-1"
      data-testid={`imperdible-${event.id}`}
    >
      {event.main_image ? (
        <img
          src={optimizedImageUrl(event.main_image, 400)}
          srcSet={optimizedSrcSet(event.main_image, [400, 800])}
          sizes="280px"
          alt={event.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
          decoding="async"
          style={event.image_position ? { objectPosition: `center ${event.image_position}` } : undefined}
          onError={handleEventImageError}
        />
      ) : (
        <img
          src={DEFAULT_EVENT_IMAGE}
          alt={event.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
          decoding="async"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />

      <span className={`absolute top-3 left-3 ${badge.cls} text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-md inline-flex items-center gap-1 shadow-lg`}>
        {badge.live && (
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
          </span>
        )}
        {badge.label}
      </span>

      <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white/80 hover:text-pink-400 transition-colors">
        <Heart className="w-4 h-4" />
      </span>

      <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
        {event.category_name && (
          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-400 mb-1.5 truncate">
            {translateText(event.category_name)}
          </p>
        )}
        <h3 className="text-base font-bold text-white leading-tight line-clamp-2 mb-2.5">
          {translateText(event.name)}
        </h3>
        <p className="text-xs text-neutral-300 flex items-center gap-1.5 mb-1">
          <Clock className="w-3.5 h-3.5 text-neutral-400" />
          {formatEventTime(event, translateText)}
        </p>
        {event.commune_name && (
          <p className="text-xs text-neutral-300 flex items-center gap-1.5 truncate">
            <MapPin className="w-3.5 h-3.5 text-neutral-400" />
            <span className="truncate">
              {event.commune_name}
              {event.region_name && event.region_name !== event.commune_name && (
                <span className="text-neutral-400">, {event.region_name}</span>
              )}
            </span>
          </p>
        )}
      </div>
    </Link>
  );
};

// ---- Horizontal rail with arrows ----
const HorizontalRail = ({ children, testId, autoplay = false, interval = 5000 }) => {
  const ref = useRef(null);
  const pausedRef = useRef(false);
  const resumeTimerRef = useRef(null);

  const scroll = (dir) => {
    if (!ref.current) return;
    const el = ref.current;
    const step = 320;

    // Loop logic: when advancing past the end, jump back to start
    if (dir > 0) {
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
      if (atEnd) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
    } else if (dir < 0 && el.scrollLeft <= 8) {
      // Going back from the start: jump to the end
      el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
      return;
    }

    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const handleManualScroll = (dir) => {
    scroll(dir);
    if (!autoplay) return;
    // Pause autoplay briefly so the user can read after clicking, then resume
    pausedRef.current = true;
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      pausedRef.current = false;
    }, 6000);
  };

  useEffect(() => {
    if (!autoplay) return;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      if (!ref.current) return;
      const el = ref.current;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
      if (atEnd) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: 320, behavior: 'smooth' });
      }
    }, interval);
    return () => {
      clearInterval(id);
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
  }, [autoplay, interval]);

  const handleMouseEnter = () => { if (autoplay) pausedRef.current = true; };
  const handleMouseLeave = () => {
    if (!autoplay) return;
    // Clear any pending resume timer and resume immediately on mouse leave
    if (resumeTimerRef.current) { clearTimeout(resumeTimerRef.current); resumeTimerRef.current = null; }
    pausedRef.current = false;
  };

  return (
    <div className="relative" data-testid={testId} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div
        ref={ref}
        className="flex gap-3 md:gap-4 overflow-x-auto pb-2 scroll-smooth snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <style>{`[data-testid="${testId}"] ::-webkit-scrollbar { display: none; }`}</style>
        {children}
      </div>
      <button
        onClick={() => handleManualScroll(-1)}
        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 w-10 h-10 rounded-full bg-neutral-900/80 backdrop-blur ring-1 ring-white/15 items-center justify-center text-white hover:bg-neutral-800 transition-colors z-10"
        aria-label="Anterior"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={() => handleManualScroll(1)}
        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 w-10 h-10 rounded-full bg-neutral-900/80 backdrop-blur ring-1 ring-white/15 items-center justify-center text-white hover:bg-neutral-800 transition-colors z-10"
        aria-label="Siguiente"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};

// ---- Search Bar (region + commune + date + category + button) ----
const HeroSearchBar = ({ regions, communes, categories, dateOptions, onSubmit, selectedCommune, selectedRegion, onRegionChange, dateFilter, setDateFilter, communeId, setCommuneId, categoryId, setCategoryId }) => {
  const { translateText } = useTranslate();
  const selectedDate = dateOptions.find((d) => d.value === dateFilter) || dateOptions[0];
  const selectedCat = categories.find((c) => c.id === categoryId);
  const selectedCom = communes.find((c) => c.id === communeId) || selectedCommune;

  return (
    <div className="relative max-w-[1440px] mx-auto -mt-8 md:-mt-12 px-4 md:px-0 z-20">
      <div className="bg-neutral-900/90 backdrop-blur-xl ring-1 ring-white/10 rounded-2xl md:rounded-full shadow-2xl shadow-black/40 p-3 md:p-2 md:pl-2 flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-1">
        {/* Region */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 px-4 py-3 md:py-3 rounded-xl md:rounded-full text-left text-sm font-medium text-white hover:bg-white/5 transition-colors flex-1 min-w-0"
              data-testid="search-region-trigger"
            >
              <Globe className="w-4 h-4 text-[#00CC66] shrink-0" />
              <span className="truncate">{selectedRegion?.name || translateText('Todas las regiones')}</span>
              <ChevronRight className="w-4 h-4 text-neutral-500 ml-auto rotate-90 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto w-64">
            {(regions || []).map((r) => (
              <DropdownMenuItem key={r.id} onClick={() => onRegionChange(r)} data-testid={`region-option-${r.id}`}>{r.name}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="hidden md:block w-px h-8 bg-white/10" />

        {/* Commune */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 px-4 py-3 md:py-3 rounded-xl md:rounded-full text-left text-sm font-medium text-white hover:bg-white/5 transition-colors flex-1 min-w-0"
              data-testid="search-commune-trigger"
            >
              <MapPin className="w-4 h-4 text-[#00CC66] shrink-0" />
              <span className="truncate">{selectedCom?.name || translateText('Todas las comunas')}</span>
              <ChevronRight className="w-4 h-4 text-neutral-500 ml-auto rotate-90 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto w-64">
            <DropdownMenuItem onClick={() => setCommuneId('')}>{translateText('Todas las comunas')}</DropdownMenuItem>
            {communes.map((c) => (
              <DropdownMenuItem key={c.id} onClick={() => setCommuneId(c.id)}>{c.name}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="hidden md:block w-px h-8 bg-white/10" />

        {/* Date */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 px-4 py-3 rounded-xl md:rounded-full text-left text-sm font-medium text-white hover:bg-white/5 transition-colors flex-1 min-w-0"
              data-testid="search-date-trigger"
            >
              <Calendar className="w-4 h-4 text-[#00CC66] shrink-0" />
              <span className="truncate">{selectedDate.label}</span>
              <ChevronRight className="w-4 h-4 text-neutral-500 ml-auto rotate-90 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {dateOptions.map((d) => (
              <DropdownMenuItem key={d.value} onClick={() => setDateFilter(d.value)}>{d.label}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="hidden md:block w-px h-8 bg-white/10" />

        {/* Category */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 px-4 py-3 rounded-xl md:rounded-full text-left text-sm font-medium text-white hover:bg-white/5 transition-colors flex-1 min-w-0"
              data-testid="search-category-trigger"
            >
              <LayoutGrid className="w-4 h-4 text-[#00CC66] shrink-0" />
              <span className="truncate">{selectedCat?.name ? translateText(selectedCat.name) : translateText('Todas las categorías')}</span>
              <ChevronRight className="w-4 h-4 text-neutral-500 ml-auto rotate-90 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto w-64">
            <DropdownMenuItem onClick={() => setCategoryId('')}>{translateText('Todas las categorías')}</DropdownMenuItem>
            {categories.map((c) => (
              <DropdownMenuItem key={c.id} onClick={() => setCategoryId(c.id)}>{translateText(c.name)}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          onClick={onSubmit}
          className="bg-[#00CC66] hover:bg-[#00b85b] text-neutral-900 font-bold rounded-xl md:rounded-full px-6 py-3 md:py-5 shadow-lg shadow-[#00CC66]/30 transition-all hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
          data-testid="search-submit-btn"
        >
          {translateText('Buscar panoramas')}
          <Search className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// ---- Main Landing ----
export const Landing = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { translateText } = useTranslate();
  const { selectedCommune, selectedRegion, loading: territoryLoading, communes: territoryCommunes, regions, selectRegion } = useTerritory();
  const { isDark } = useTheme();

  // Territory-scoped cache key so switching commune/region doesn't leak data.
  const cacheScope = selectedCommune?.id ? `c:${selectedCommune.id}` : (selectedRegion?.id ? `r:${selectedRegion.id}` : 'all');
  const cachedToday = readCache(`landing:today:${cacheScope}`);
  const cachedFeatured = readCache(`landing:featured:${cacheScope}`);
  const cachedLatest = readCache(`landing:latest:${cacheScope}`);

  const [todayEvents, setTodayEvents] = useState(cachedToday || []);
  const [featuredEvents, setFeaturedEvents] = useState(cachedFeatured || []);
  const [latestEvents, setLatestEvents] = useState(cachedLatest || []);
  const [categories, setCategories] = useState([]);
  const [allCommunes, setAllCommunes] = useState([]);
  const [loadingToday, setLoadingToday] = useState(!cachedToday);
  const [loadingFeatured, setLoadingFeatured] = useState(!cachedFeatured);
  const [loadingLatest, setLoadingLatest] = useState(!cachedLatest);
  const [socialProof, setSocialProof] = useState({
    count: 2500,
    photos: [
      'https://images.unsplash.com/photo-1542514616-c0cd93ca9563?w=160&h=160&fit=crop&crop=faces&q=70',
      'https://images.unsplash.com/photo-1611344238461-993b36fe36fb?w=160&h=160&fit=crop&crop=faces&q=70',
      'https://images.unsplash.com/photo-1545959570-a94084071b5d?w=160&h=160&fit=crop&crop=faces&q=70',
      'https://images.unsplash.com/photo-1689418335797-83999272bc94?w=160&h=160&fit=crop&crop=faces&q=70',
    ],
  });

  // Search state
  const [communeId, setCommuneId] = useState(selectedCommune?.id || '');
  const [categoryId, setCategoryId] = useState('');
  const [dateFilter, setDateFilter] = useState('any');

  const dateOptions = useMemo(() => {
    const today = new Date();
    const fmt = (d) => d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', timeZone: 'America/Santiago' }).replace('.', '');
    return [
      { value: 'today', label: `${translateText('Hoy')}, ${fmt(today)}` },
      { value: 'tomorrow', label: translateText('Mañana') },
      { value: 'weekend', label: translateText('Este fin de semana') },
      { value: 'week', label: translateText('Esta semana') },
      { value: 'any', label: translateText('Cualquier fecha') },
    ];
  }, [translateText]);

  useEffect(() => {
    setCommuneId(selectedCommune?.id || '');
  }, [selectedCommune?.id]);

  useEffect(() => {
    fetchCategories();
    fetchCommunes();
    fetchSocialProof();
  }, []);

  const fetchSocialProof = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/uploads/social-proof`);
      if (res.data) setSocialProof(res.data);
    } catch (e) { console.error('social-proof failed', e); }
  };

  useEffect(() => {
    if (territoryLoading) return;
    // Show cached data instantly if present; otherwise show skeleton until fetch resolves.
    const t = readCache(`landing:today:${cacheScope}`);
    const f = readCache(`landing:featured:${cacheScope}`);
    const l = readCache(`landing:latest:${cacheScope}`);
    if (t) { setTodayEvents(t); setLoadingToday(false); } else { setLoadingToday(true); }
    if (f) { setFeaturedEvents(f); setLoadingFeatured(false); } else { setLoadingFeatured(true); }
    if (l) { setLatestEvents(l); setLoadingLatest(false); } else { setLoadingLatest(true); }
    fetchTodayEvents();
    fetchFeaturedEvents();
    fetchLatestEvents();
  }, [selectedCommune, selectedRegion, territoryLoading]);

  const fetchTodayEvents = async () => {
    try {
      const params = new URLSearchParams({ limit: '12' });
      if (selectedCommune?.id) params.set('commune_id', selectedCommune.id);
      else if (selectedRegion?.id) params.set('region_id', selectedRegion.id);
      const res = await axios.get(`${API_URL}/api/events/today?${params}`);
      const data = res.data || [];
      setTodayEvents(data);
      writeCache(`landing:today:${cacheScope}`, data);
    } catch (e) { console.error('today events failed', e); }
    finally { setLoadingToday(false); }
  };

  const fetchFeaturedEvents = async () => {
    try {
      const params = new URLSearchParams({ limit: '8', featured_type: 'carousel' });
      if (selectedCommune?.id) params.set('commune_id', selectedCommune.id);
      else if (selectedRegion?.id) params.set('region_id', selectedRegion.id);
      const res = await axios.get(`${API_URL}/api/events/featured?${params}`);
      const data = res.data || [];
      setFeaturedEvents(data);
      writeCache(`landing:featured:${cacheScope}`, data);
    } catch (e) { console.error('featured failed', e); }
    finally { setLoadingFeatured(false); }
  };

  const fetchLatestEvents = async () => {
    try {
      const params = new URLSearchParams({ limit: '12' });
      if (selectedCommune?.id) params.set('commune_id', selectedCommune.id);
      else if (selectedRegion?.id) params.set('region_id', selectedRegion.id);
      const res = await axios.get(`${API_URL}/api/events/latest?${params}`);
      const data = res.data || [];
      setLatestEvents(data);
      writeCache(`landing:latest:${cacheScope}`, data);
    } catch (e) { console.error('latest events failed', e); }
    finally { setLoadingLatest(false); }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/categories`);
      setCategories(res.data || []);
    } catch (e) { console.error('categories failed', e); }
  };

  const fetchCommunes = async () => {
    try {
      if (territoryCommunes && territoryCommunes.length > 0) {
        setAllCommunes(territoryCommunes);
        return;
      }
      if (selectedRegion?.id) {
        const res = await axios.get(`${API_URL}/api/territories/communes?region_id=${selectedRegion.id}&has_events=true`);
        setAllCommunes(res.data || []);
      }
    } catch (e) { console.error('communes failed', e); }
  };

  // Keep local `allCommunes` in sync with the context's commune list. This runs
  // every time the user switches region (context refetches communes for that region).
  useEffect(() => {
    setAllCommunes(territoryCommunes || []);
  }, [territoryCommunes]);

  const liveEvents = useMemo(() => {
    const live = todayEvents.filter(isEventLiveNow);
    const others = todayEvents.filter((e) => !isEventLiveNow(e));
    return [...live, ...others];
  }, [todayEvents]);

  const liveCount = useMemo(() => todayEvents.filter(isEventLiveNow).length, [todayEvents]);

  // Imperdibles: strictly events flagged as `featured_type=carousel`. We do
  // NOT fall back to today's events any more — the visitor (and the provider
  // who owns them) would otherwise see events labelled as "Imperdibles" that
  // were never actually marked as such in the dashboard.
  const imperdibles = featuredEvents;

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (communeId) params.set('commune_id', communeId);
    if (categoryId) params.set('category', categoryId);
    if (dateFilter && dateFilter !== 'any') params.set('date_filter', dateFilter);
    navigate(`/explore?${params.toString()}`);
  };

  const communeName = selectedCommune?.name || selectedRegion?.name || '';
  const hasLocation = Boolean(communeName);

  const landingJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        name: 'PlanCity',
        url: 'https://plancity.cl',
        description: 'Directorio de panoramas, eventos, conciertos, escapadas y actividades locales en Chile',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://plancity.cl/explore?search={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        name: 'PlanCity',
        url: 'https://plancity.cl',
      },
    ],
  };

  return (
    <div className="min-h-screen text-white relative" data-testid="landing-page" style={{ zIndex: 1 }}>
      <SEO
        path="/"
        description="Descubre los mejores panoramas, conciertos, festivales, escapadas, ferias gastronómicas y actividades en tu comuna. Encuentra qué hacer hoy cerca de ti en Chile."
        jsonLd={landingJsonLd}
      />
      <style>{`.plancity-wrap { max-width: 1440px; margin-left: auto; margin-right: auto; }`}</style>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        {/* Backdrop - subtle ambient glows only, lets the starfield show through */}
        <div className="absolute inset-0 -z-10 pointer-events-none">
          {/* Ambient green glow top-left */}
          <div
            className="absolute -top-40 -left-32 w-[600px] h-[600px] rounded-full blur-3xl opacity-25"
            style={{ background: 'radial-gradient(circle, rgba(0,204,102,0.6) 0%, rgba(0,204,102,0) 70%)' }}
          />
          {/* Ambient red glow top-right */}
          <div
            className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
            style={{ background: 'radial-gradient(circle, rgba(239,68,68,0.6) 0%, rgba(239,68,68,0) 70%)' }}
          />
          {/* Soft purple glow bottom-center for depth */}
          <div
            className="absolute bottom-0 left-1/3 w-[700px] h-[400px] rounded-full blur-3xl opacity-15"
            style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.6) 0%, rgba(139,92,246,0) 70%)' }}
          />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.25fr] gap-10 lg:gap-12 items-center">
            {/* LEFT - Title + CTAs */}
            <div className="animate-slideUp min-w-0">
              <h1 className="text-[34px] leading-[1.05] sm:text-5xl lg:text-[48px] xl:text-[56px] font-extrabold tracking-tight text-white">
                {translateText('¿Qué está pasando')}{' '}
                <span className="relative inline-block whitespace-nowrap">
                  <span className="relative z-10 bg-gradient-to-r from-red-500 via-orange-500 to-orange-400 bg-clip-text text-transparent">
                    {translateText('hoy')}
                  </span>
                  {/* Smooth swoosh underline (red marker style) */}
                  <svg
                    className="absolute left-[-8%] right-[-8%] -bottom-3 md:-bottom-4 w-[116%] h-3 md:h-4 pointer-events-none"
                    viewBox="0 0 120 14"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M 3 11 Q 55 1 117 6"
                      stroke="#ef4444"
                      strokeWidth="3.2"
                      strokeLinecap="round"
                      fill="none"
                    />
                  </svg>
                </span>
                <span className="text-white"> {translateText('en Chile')}?</span>
              </h1>

              <p className="mt-6 md:mt-8 text-base md:text-lg text-neutral-300 max-w-xl leading-relaxed">
                {translateText('Conciertos, panoramas, talleres, festivales, experiencias y mucho más.')}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={() => navigate('/explore?date_filter=today')}
                  className="bg-[#00CC66] hover:bg-[#00b85b] text-neutral-900 font-bold rounded-full text-base md:text-lg px-7 py-6 shadow-2xl shadow-[#00CC66]/40 hover:-translate-y-0.5 transition-all"
                  data-testid="hero-cta-today"
                >
                  {translateText('Ver eventos de hoy')}
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/map')}
                  className="rounded-full bg-transparent border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/60 text-base md:text-lg px-7 py-6 font-semibold"
                  data-testid="hero-cta-map"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  {translateText('Explorar mapa')}
                </Button>
              </div>
            </div>

            {/* RIGHT - Live events rail */}
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-4">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00CC66] opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#00CC66]" />
                </span>
                <span className="text-xs md:text-sm font-extrabold uppercase tracking-wider text-white" data-testid="live-count-label">
                  {liveCount > 0
                    ? `${liveCount} ${liveCount === 1 ? translateText('EVENTO') : translateText('EVENTOS')} ${translateText('EN VIVO AHORA')}`
                    : translateText('EVENTOS DE HOY')}
                </span>
              </div>

              {loadingToday ? (
                <div className="flex gap-3 overflow-hidden">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="w-[220px] md:w-[240px] h-[320px] md:h-[350px] rounded-2xl shrink-0 bg-white/5" />
                  ))}
                </div>
              ) : liveEvents.length > 0 ? (
                <HorizontalRail testId="live-events-rail" autoplay interval={5000}>
                  {liveEvents.slice(0, 12).map((ev) => (
                    <div key={ev.id} className="snap-start">
                      <LiveEventCard event={ev} />
                    </div>
                  ))}
                </HorizontalRail>
              ) : (
                <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-8 text-center">
                  <Calendar className="w-10 h-10 mx-auto text-neutral-500 mb-3" />
                  <p className="text-neutral-400 text-sm">{translateText('No hay eventos hoy en esta zona todavía.')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== SEARCH BAR (floating, overlaps hero/categories) ===== */}
      <HeroSearchBar
        regions={regions}
        communes={allCommunes}
        categories={categories}
        dateOptions={dateOptions}
        onSubmit={handleSearch}
        selectedCommune={selectedCommune}
        selectedRegion={selectedRegion}
        onRegionChange={(r) => { selectRegion(r); setCommuneId(''); }}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        communeId={communeId}
        setCommuneId={setCommuneId}
        categoryId={categoryId}
        setCategoryId={setCategoryId}
      />

      {/* ===== CATEGORIES (circular icons) ===== */}
      <section className="relative pt-16 md:pt-20 pb-12 md:pb-16">
        <div className="plancity-wrap px-4 md:px-8">
          <div className="flex items-end justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              {translateText('Explora por categorías')}
            </h2>
            <button
              onClick={() => navigate('/explore')}
              className="text-sm font-semibold text-neutral-300 hover:text-[#00CC66] inline-flex items-center gap-1 transition-colors"
              data-testid="categories-view-all"
            >
              {translateText('Ver todas')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="flex md:grid md:grid-cols-9 gap-3 md:gap-5 overflow-x-auto pb-3 md:pb-0" style={{ scrollbarWidth: 'none' }}>
            {categories.slice(0, 9).map((cat, idx) => {
              const { Icon, bg, shadow, hexColor } = getCategoryStyle(cat);
              const useHex = !!hexColor;
              return (
                <button
                  key={cat.id}
                  onClick={() => navigate(`/explore?category=${cat.id}`)}
                  className="group flex flex-col items-center gap-2.5 min-w-[88px] md:min-w-0 animate-fadeIn"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                  data-testid={`category-${cat.id}`}
                >
                  <span
                    className={`flex items-center justify-center w-16 h-16 md:w-[72px] md:h-[72px] rounded-full ${useHex ? '' : bg} text-white shadow-xl ${useHex ? '' : shadow} group-hover:scale-110 group-hover:-translate-y-1 transition-all duration-300`}
                    style={useHex ? { backgroundColor: hexColor, boxShadow: `0 12px 24px -6px ${hexColor}66, 0 4px 12px -2px ${hexColor}40` } : undefined}
                  >
                    <Icon className="w-7 h-7 md:w-8 md:h-8" strokeWidth={2.2} />
                  </span>
                  <span className="text-xs md:text-[13px] font-semibold text-center text-white leading-tight max-w-[100px]">
                    {translateText(cat.name)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== IMPERDIBLES DE HOY ===== */}
      <section className="relative pb-16 md:pb-20">
        <div className="plancity-wrap px-4 md:px-8">
          <div className="flex items-end justify-between mb-6 md:mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                <span className="relative inline-block">
                  {translateText('Imperdibles')}
                  <svg
                    className="absolute left-[-4%] right-[-4%] -bottom-2 w-[108%] h-2.5 pointer-events-none"
                    viewBox="0 0 120 10"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M 3 8 Q 55 1 117 4"
                      stroke="#ef4444"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      fill="none"
                    />
                  </svg>
                </span>
              </h2>
            </div>
            <button
              onClick={() => navigate('/explore?date_filter=today')}
              className="text-sm font-semibold text-neutral-300 hover:text-[#00CC66] inline-flex items-center gap-1 transition-colors"
              data-testid="imperdibles-view-all"
            >
              {translateText('Ver todos los eventos')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {loadingFeatured && loadingToday ? (
            <div className="flex gap-4 overflow-hidden">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="w-[260px] md:w-[280px] h-[340px] rounded-2xl shrink-0 bg-white/5" />
              ))}
            </div>
          ) : imperdibles.length > 0 ? (
            <HorizontalRail testId="imperdibles-rail">
              {imperdibles.slice(0, 12).map((ev) => (
                <div key={ev.id} className="snap-start">
                  <ImperdibleCard event={ev} />
                </div>
              ))}
            </HorizontalRail>
          ) : (
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-12 text-center">
              <Calendar className="w-12 h-12 mx-auto text-neutral-500 mb-3" />
              <h3 className="text-lg font-semibold text-neutral-200 mb-1">{t('noEvents')}</h3>
              <p className="text-neutral-400 text-sm">{translateText('No hay imperdibles en esta zona por ahora.')}</p>
            </div>
          )}
        </div>
      </section>

      {/* ===== ÚLTIMOS EVENTOS PUBLICADOS ===== */}
      <section className="relative pb-16 md:pb-20">
        <div className="plancity-wrap px-4 md:px-8">
          <div className="flex items-end justify-between mb-6 md:mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                <span className="relative inline-block">
                  {translateText('Últimos publicados')}
                  <svg
                    className="absolute left-[-4%] right-[-4%] -bottom-2 w-[108%] h-2.5 pointer-events-none"
                    viewBox="0 0 120 10"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M 3 8 Q 55 1 117 4"
                      stroke="#00CC66"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      fill="none"
                    />
                  </svg>
                </span>
              </h2>
              <p className="text-sm text-neutral-400 mt-2">
                {translateText('Lo más reciente subido por nuestros organizadores.')}
              </p>
            </div>
            <button
              onClick={() => navigate('/explore?sort=recent')}
              className="text-sm font-semibold text-neutral-300 hover:text-[#00CC66] inline-flex items-center gap-1 transition-colors"
              data-testid="latest-view-all"
            >
              {translateText('Ver todos los eventos')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {loadingLatest ? (
            <div className="flex gap-4 overflow-hidden">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="w-[260px] md:w-[280px] h-[340px] rounded-2xl shrink-0 bg-white/5" />
              ))}
            </div>
          ) : latestEvents.length > 0 ? (
            <HorizontalRail testId="latest-rail">
              {latestEvents.slice(0, 12).map((ev) => (
                <div key={ev.id} className="snap-start">
                  <ImperdibleCard event={ev} />
                </div>
              ))}
            </HorizontalRail>
          ) : (
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-12 text-center">
              <Calendar className="w-12 h-12 mx-auto text-neutral-500 mb-3" />
              <h3 className="text-lg font-semibold text-neutral-200 mb-1">{t('noEvents')}</h3>
              <p className="text-neutral-400 text-sm">{translateText('Aún no hay eventos publicados en esta zona.')}</p>
            </div>
          )}
        </div>
      </section>

      {/* ===== TRUST BAR ===== */}
      <section className="relative py-10 border-t border-white/5">
        <div className="plancity-wrap px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-3 min-w-0">
              {/* Avatar stack - dynamic from admin (4 photos) */}
              <div className="flex -space-x-3 shrink-0">
                {socialProof.photos.slice(0, 4).map((src, i) => {
                  const fullSrc = src && src.startsWith('/') ? `${API_URL}${src}` : src;
                  return (
                    <img
                      key={i}
                      src={optimizedImageUrl(fullSrc, 200)}
                      alt={`Usuario ${i + 1} de PlanCity`}
                      className="w-9 h-9 rounded-full object-cover ring-2 ring-[#0a0f1c]"
                      loading="lazy"
                      decoding="async"
                      width="36"
                      height="36"
                    />
                  );
                })}
              </div>
              <p className="text-sm text-neutral-300 leading-snug">
                {translateText('Únete a más de')}{' '}
                <span className="font-bold text-white">
                  {(socialProof.count || 0).toLocaleString('es-CL')} {translateText('personas')}
                </span>{' '}
                <span className="block md:inline">{translateText('que ya descubren los mejores panoramas en PlanCity')}</span>
              </p>
            </div>
            <div className="flex flex-nowrap items-center gap-x-6 lg:gap-x-8 gap-y-2 text-sm text-neutral-400 overflow-x-auto md:overflow-visible">
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <RefreshCw className="w-4 h-4 text-[#00CC66]" />
                {translateText('Actualizado todos los días')}
              </span>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <ShieldCheck className="w-4 h-4 text-[#00CC66]" />
                {translateText('Eventos verificados')}
              </span>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <UsersRound className="w-4 h-4 text-[#00CC66]" />
                {translateText('Organizadores locales')}
              </span>
            </div>
          </div>
        </div>
      </section>

      <SeoLinks />

      {/* ===== CTA Providers (kept) ===== */}
      <section className="py-16 bg-[#064E3B]">
        <div className="plancity-wrap px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('providerCTA')}</h2>
          <p className="text-green-100 text-lg mb-8 max-w-2xl mx-auto">{t('providerCTASubtitle')}</p>
          <Button
            className="bg-white text-[#064E3B] hover:bg-green-50 font-semibold rounded-full px-8 py-6 text-lg"
            onClick={() => navigate('/register?type=provider')}
            data-testid="cta-provider-btn"
          >
            {t('providerCTAButton')}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
